import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { toast } from 'sonner';
import { Mail, Lock, ArrowLeft, Globe, Eye, EyeOff, GraduationCap } from 'lucide-react';

const StudentAuthPage = () => {
  const navigate = useNavigate();
  const { login, loginChild } = useAuth();
  const { language, toggleLanguage, localize } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginType, setLoginType] = useState('email'); // 'email' or 'username'
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      let user;
      if (loginType === 'username') {
        // Child login with username — use AuthContext so app state stays in sync
        user = await loginChild(formData.username, formData.password);
      } else {
        // Regular email login
        user = await login(formData.email, formData.password);
        if (user.role !== 'student') {
          toast.error(localize({ ar: 'هذا الحساب ليس حساب طالب', en: 'This is not a student account' }));
          return;
        }
      }
      
      toast.success(localize({ ar: 'تم تسجيل الدخول بنجاح', en: 'Login successful' }));
      navigate('/student');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const msg = (detail && (detail.ar || detail.en)) || error.message || (localize({ ar: 'فشل تسجيل الدخول', en: 'Login failed' }));
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen gradient-hero flex items-center justify-center p-6 ${language === 'ar' ? 'rtl' : 'ltr'}`} dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/login" className="inline-flex items-center gap-3">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-r from-emerald-500 to-emerald-600 flex items-center justify-center">
              <GraduationCap className="w-8 h-8 text-white" />
            </div>
            <div className="text-left">
              <span className="text-2xl font-bold text-emerald-600">
                {localize({ ar: 'دخول الطالب', en: 'Student Login' })}
              </span>
              <span className="block text-xs text-slate-500">Learn With Trust</span>
            </div>
          </Link>
        </div>

        <Card className="border-0 shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-end mb-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleLanguage}
                className="text-slate-600 hover:text-emerald-600"
              >
                <Globe className="w-4 h-4 mr-1" />
                {language === 'ar' ? 'English' : 'العربية'}
              </Button>
            </div>
            <CardTitle className="text-2xl font-bold text-slate-900">
              {localize({ ar: 'مرحباً بك', en: 'Welcome' })}
            </CardTitle>
            <CardDescription className="text-slate-500">
              {localize({ ar: 'سجل دخولك للوصول إلى دروسك', en: 'Login to access your lessons' })}
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            {/* Login Type Toggle */}
            <div className="flex gap-2 p-1 bg-slate-100 rounded-xl mb-6">
              <button
                type="button"
                onClick={() => setLoginType('email')}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                  loginType === 'email' 
                    ? 'bg-white shadow text-emerald-600' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {localize({ ar: 'بالبريد الإلكتروني', en: 'With Email' })}
              </button>
              <button
                type="button"
                onClick={() => setLoginType('username')}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                  loginType === 'username' 
                    ? 'bg-white shadow text-emerald-600' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {localize({ ar: 'باسم المستخدم', en: 'With Username' })}
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {loginType === 'email' ? (
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700">
                    {localize({ ar: 'البريد الإلكتروني', en: 'Email' })}
                  </Label>
                  <div className="relative">
                    <Mail className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400`} />
                    <Input
                      id="email"
                      type="email"
                      placeholder="example@email.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className={`${language === 'ar' ? 'pr-10' : 'pl-10'} h-12 rounded-xl border-slate-200 focus:border-emerald-500`}
                      required={loginType === 'email'}
                      data-testid="student-email-input"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="username" className="text-slate-700">
                    {localize({ ar: 'اسم المستخدم', en: 'Username' })}
                  </Label>
                  <div className="relative">
                    <GraduationCap className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400`} />
                    <Input
                      id="username"
                      type="text"
                      placeholder={localize({ ar: 'أدخل اسم المستخدم', en: 'Enter username' })}
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      className={`${language === 'ar' ? 'pr-10' : 'pl-10'} h-12 rounded-xl border-slate-200 focus:border-emerald-500`}
                      required={loginType === 'username'}
                      data-testid="student-username-input"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700">
                  {localize({ ar: 'كلمة المرور', en: 'Password' })}
                </Label>
                <div className="relative">
                  <Lock className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400`} />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className={`${language === 'ar' ? 'pr-10 pl-10' : 'pl-10 pr-10'} h-12 rounded-xl border-slate-200 focus:border-emerald-500`}
                    required
                    data-testid="student-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute ${language === 'ar' ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600`}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 btn-glass btn-glass-emerald rounded-xl font-bold text-base tracking-wide"
                disabled={loading}
                data-testid="student-login-submit"
              >
                {loading 
                  ? (localize({ ar: 'جاري الدخول...', en: 'Logging in...' })) 
                  : (localize({ ar: 'دخول', en: 'Login' }))
                }
              </Button>
            </form>

            {loginType === 'email' && (
              <div className="mt-4 text-center">
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-emerald-600 hover:text-emerald-700"
                >
                  {localize({ ar: 'نسيت كلمة المرور؟', en: 'Forgot password?' })}
                </Link>
              </div>
            )}

            <div className="mt-6 p-4 bg-emerald-50 rounded-xl">
              <p className="text-sm text-emerald-700 text-center">
                {localize({ ar: '💡 إذا كان عمرك أقل من 18 سنة، يمكن لولي أمرك إنشاء حساب لك', en: '💡 If you are under 18, your parent can create an account for you' })
                }
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="text-center mt-6">
          <Link 
            to="/login" 
            className="inline-flex items-center gap-2 text-slate-600 hover:text-emerald-600 transition-colors"
          >
            <ArrowLeft className={`w-4 h-4 ${language === 'ar' ? '' : 'rotate-180'}`} />
            {localize({ ar: 'العودة لاختيار نوع الحساب', en: 'Back to account selection' })}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default StudentAuthPage;
